/**
 * Browser-side image normalisation helpers (no dependencies).
 *
 * The favicon is special: a browser tab icon must be a small SQUARE bitmap, and
 * shipping whatever aspect ratio the admin happened to pick (a wide logo, a
 * portrait photo, a non-square JPEG from the CDN) is exactly what makes a
 * favicon look stretched or non-standard. `toSquarePngFile` centre-crops the
 * chosen image to a square and rescales it to a crisp, standard size, encoded
 * as PNG — the format every browser renders reliably as a tab icon.
 */

/** Standard favicon edge length in pixels (browser tab icon). */
export const FAVICON_SIZE = 32;

function loadImageElement(file: File): Promise<{ img: HTMLImageElement; url: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read the selected image. Please try a PNG, JPG or WebP file."));
    };
    img.src = url;
  });
}

/**
 * Centre-crops `file` to a square and resizes it to `size` x `size`, returning
 * a new PNG `File`. Falls back to the original file if the canvas API is
 * unavailable, so an upload is never blocked by an encoding hiccup.
 */
export async function toSquarePngFile(file: File, size: number = FAVICON_SIZE, name = "favicon.png"): Promise<File> {
  if (typeof document === "undefined") return file;

  const { img, url } = await loadImageElement(file);
  try {
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) return file;

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    // Crop the largest centred square, then draw it into a size x size canvas.
    const side = Math.min(width, height);
    const sx = (width - side) / 2;
    const sy = (height - side) / 2;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) return file;
    return new File([blob], name, { type: "image/png" });
  } finally {
    URL.revokeObjectURL(url);
  }
}